import React from 'react';
import * as util from '#packages/util';
import * as baseUI from '#packages/baseUI';
import constants from '#packages/constants';
import { LeftBar } from '#packages/leftBar';
import LazyComponent from '#packages/lazyComponent';
import OpenedPanels from '../panels/openedPanels';
import { IDEContainer } from '../ideContainer';
import {
  mapDispatchToProps,
  mapStateToProps,
  type SplitterWrapperDispatchProps,
  type SplitterWrapperStateProps,
} from './splitterWrapperMapper';

import experiment from 'experiment';
import EditingArea from './editingArea/editingArea';

const MOBILE_WIZARD_OPENING_STATE =
  constants.MOBILE.MOBILE_WIZARD.OPENING_STATE;

const Splitter = baseUI.splitter as any;
const withLeftPanelHiddenElementsRedesignExperiment =
  experiment.getValue('se_leftPanelHiddenMobileElementsRedesign') === 'B' ||
  experiment.getValue('se_leftPanelHiddenMobileElementsRedesign') === 'C';

export interface SplitterWrapperOwnProps {}

interface SplitterWrapperProps
  extends SplitterWrapperOwnProps,
    SplitterWrapperStateProps,
    SplitterWrapperDispatchProps {}

const getWixCodeTreeComponent = (props: SplitterWrapperProps) => {
  if (!props.loadWixCodeWithRepluggableInClassic) {
    return (
      <LazyComponent
        moduleName={
          util.appStudioUtils.isAppStudio()
            ? 'wixCode.components.DeprecatedAppBuilderTree'
            : 'wixCode.components.Tree'
        }
        key="wix-code-tree"
        id="wix-code-tree"
        performingMouseMoveAction={props.performingMouseMoveAction}
        context={props.devModeContextWithData}
      />
    );
  }
  return (
    <props.veloTreeComponent
      performingMouseMoveAction={props.performingMouseMoveAction}
      context={props.devModeContextWithData}
    />
  );
};

const getConsoleComponent = (props: SplitterWrapperProps) => {
  if (!props.loadWixCodeWithRepluggableInClassic) {
    return (
      <LazyComponent
        moduleName="wixCode.components.console"
        key="wix-code-console"
        id="wix-code-console"
      />
    );
  }

  return <props.consoleComponent />;
};

class SplitterWrapperComponent extends React.Component<SplitterWrapperProps> {
  static displayName = 'SplitterWrapper';

  private leftBarAreaRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.props.initDomMeasurements();
  }

  componentDidUpdate(prevProps: SplitterWrapperProps) {
    this.props.onRenderDone(this.props.renderCounter);

    if (prevProps.isMobileEditor !== this.props.isMobileEditor) {
      const switchEditorModeInteraction = this.props.isMobileEditor
        ? util.fedopsLogger.INTERACTIONS.CHANGE_EDITOR_MODE_TO_MOBILE
        : util.fedopsLogger.INTERACTIONS.CHANGE_EDITOR_MODE_TO_DESKTOP;

      util.fedopsLogger.interactionEnded(switchEditorModeInteraction, {
        paramsOverrides: {
          sourceOfEnd: 'splitterWrapper',
          isPreview: `${this.props.previewMode}`,
          isZoomOut: `${this.props.isZoomOut}`,
        },
      });
    }

    this.props.initDomMeasurements();

    // these transitions (to Mobile, to Preview, to ZoomOut) have CSS animation, which should be ended before DOM Measurements
    // TODO probably, these is only cases when DomMeasurements are needed ever
    if (
      util.isPropChanged(prevProps, this.props, [
        'hideTools',
        'previewMode',
        'isMobileEditor',
        'isShrinkedStageZoomOutActive',
      ])
    ) {
      this.props.initDomMeasurementsAfterAnimationEnd();
    }
  }

  shouldCompletelyHideTools() {
    const isMobileWizardExpanded =
      this.props.mobileWizardOpeningState ===
      MOBILE_WIZARD_OPENING_STATE.IN_PROGRESS;
    return this.props.shouldRenderMobileWizard && isMobileWizardExpanded;
  }

  shouldShowSectionedPanel() {
    if (this.props.isRightPanelHidden) {
      return false;
    }

    if (!this.props.previewMode) {
      return (
        this.props.shouldShowDevModePanels &&
        this.props.hasOpenDockedSections &&
        this.props.shouldShowSectionedPanel
      );
    }
    return this.props.shouldShowSectionedPanelAppStudioPreview;
  }

  shouldShowPlatformSidePanel() {
    if (this.props.isRightPanelHidden) {
      return false;
    }
    return !this.props.previewMode && this.props.hasDockedPanels;
  }

  getPlatformSidePanelClasses() {
    return util.cx({
      'right-pane': true,
      'right-pane-above-modals': this.props.shouldRaiseSidePanelAboveModals,
    });
  }

  getRightAreaConstantWidth() {
    return this.props.previewMode
      ? constants.RIGHT_AREA.WIDTH_PREVIEW
      : constants.RIGHT_AREA.WIDTH;
  }

  leftTreeSplitterResize = (
    firstPanePercent: number,
    relativePosition: number,
    splitterSize: number,
  ) => {
    if (relativePosition < 150) {
      this.props.developerModeUI.treePane.toggle();
    } else {
      this.props.developerModeUI.treePane.setSplitPosition(
        firstPanePercent,
        relativePosition,
        splitterSize,
      );
    }
  };

  handleIdePaneResizeStart = () => {
    if (this.props.previewMode) {
      this.props.previewResizeStart();
    }
  };

  handleIdePaneResizeEnd = (
    firstPanePercent: AnyFixMe,
    relativePosition: AnyFixMe,
    splitterSize: AnyFixMe,
  ) => {
    const paneKey = this.props.previewMode ? 'consolePane' : 'idePane';

    if (this.props.previewMode) {
      this.props.previewResizeEnd();
    }

    this.props.developerModeUI[paneKey].setSplitPosition(
      firstPanePercent,
      relativePosition,
      splitterSize,
    );
  };

  getEditorLayoutLeftBarStyle = () => {
    return {
      '--left-bar-width': `${this.props.leftBarWidth}px`,
    } as React.CSSProperties;
  };

  getEditingAreaMinWidth = () => {
    const stageRightMargin = this.props.shouldCutEditingArea
      ? constants.UI.EDITING_AREA_RIGHT_MARGIN
      : 0;

    return (
      this.props.fixedStageWidth +
      stageRightMargin +
      util.uiUtils.getScrollbarWidth()
    );
  };

  getScrollBarWidthForSplitterRow = () => {
    if (
      !this.props.shouldRenderFixedStageTopGap ||
      (this.props.editingAreaWidth < this.getEditingAreaMinWidth() &&
        !this.props.shouldCutEditingArea)
    ) {
      return 0;
    }

    return util.uiUtils.getScrollbarWidth();
  };

  render() {
    return (
      <div
        className={util.cx({
          'editor-content-wrapper': true,
          'with-top-bar-banner': this.props.isWithTopBarBanner,
          'hidden-to-top': this.shouldCompletelyHideTools(),
          'tools-hidden':
            this.props.hideTools &&
            !this.props.hideToolsAndMaintainStagePosition,
        })}
      >
        <Splitter
          layout="vertical"
          splitPosition={this.props.treePaneSplitPosition}
          statefulResize
          onResize={this.leftTreeSplitterResize}
          className="file-pane-splitter"
          name="file-pane-splitter"
          handle={
            <Splitter.Handle
              onClick={() => this.props.developerModeUI.treePane.toggle()}
              isOpen={() => this.props.developerModeUI.treePane.isOpen()}
              automationId="corvid-site-structure-resize-handle"
            />
          }
        >
          {this.props.shouldShowLeftPane ? (
            <Splitter.Pane
              onContextMenu={util.browserUtil.preventDefaultRightClick}
              collapsed={!this.props.developerModeUI.treePane.isOpen()}
              fitWhenCollapsed
              key="left-panel-pane"
              minSize="150px"
              maxSize="400px"
              automationId="wix-code-left-pane"
              className="wix-code-left-pane"
            >
              {this.props.wixCodeLoaded && getWixCodeTreeComponent(this.props)}
            </Splitter.Pane>
          ) : null}

          <Splitter.Pane>
            <Splitter
              layout="vertical"
              splitPosition={
                !this.props.isRightPanelHidden &&
                this.shouldShowPlatformSidePanel()
                  ? null
                  : '100%'
              }
            >
              <Splitter.Pane
                minSize={`calc(100% - ${
                  this.shouldShowPlatformSidePanel()
                    ? this.props.sidePanelWidth
                    : this.getRightAreaConstantWidth()
                }px)`}
              >
                <Splitter
                  layout="horizontal"
                  splitPosition={this.props.editorSplitPosition}
                  onDividerClick={
                    this.props.previewMode
                      ? null
                      : this.props.developerModeUI.idePane.toggleMinimize
                  }
                  statefulResize
                  onDragStarted={this.handleIdePaneResizeStart}
                  onResize={this.handleIdePaneResizeEnd}
                  className="ide-splitter"
                >
                  <Splitter.Pane
                    maxSize={
                      this.props.shouldShowIdePane
                        ? `calc(100% - ${this.props.developerModeUI.topbarHeight})`
                        : '100%'
                    }
                  >
                    <Splitter
                      layout="vertical"
                      splitPosition={this.props.secondaryLeftAreaWidth}
                      statefulResize
                      onResize={this.props.setSecondaryLeftAreaWidth}
                      className="left-area-splitter"
                    >
                      {this.props.secondaryLeftAreaContentModule &&
                      !this.props.hideTools ? (
                        <Splitter.Pane
                          onContextMenu={
                            util.browserUtil.preventDefaultRightClick
                          }
                          key="secondaryLeftArea"
                          maxSize="360px"
                          minSize="135px"
                          className="secondary-left-area"
                        >
                          <LazyComponent
                            moduleName={
                              this.props.secondaryLeftAreaContentModule
                            }
                          />
                        </Splitter.Pane>
                      ) : null}

                      <Splitter.Pane>
                        <div
                          className="editor-layout-splitter-row"
                          style={
                            {
                              '--stage-scrollbar-width': `${this.getScrollBarWidthForSplitterRow()}px`,
                            } as React.CSSProperties
                          }
                        >
                          {this.props.isLeftBarVisible && (
                            <div
                              className={util.cx('editor-layout-left-bar', {
                                expanded: this.props.hasOpenedLeftPanels,
                                'left-panel-hidden-elements-redesign-experiment':
                                  withLeftPanelHiddenElementsRedesignExperiment,
                              })}
                              ref={this.leftBarAreaRef}
                              style={this.getEditorLayoutLeftBarStyle()}
                            >
                              {this.props.previewMode ? null : <LeftBar />}
                            </div>
                          )}
                          <EditingArea leftBarAreaRef={this.leftBarAreaRef} />
                          {this.props.WorkspaceRightPanel.map((slot) => (
                            <slot.contribution key={slot.uniqueId} />
                          ))}
                        </div>
                      </Splitter.Pane>
                    </Splitter>
                  </Splitter.Pane>
                  {this.props.shouldShowIdePane ? (
                    <Splitter.Pane
                      key="wix-code-ide-pane"
                      minSize={this.props.developerModeUI.topbarHeight}
                      onClick={this.props.closeAllPanels}
                      className={util.cx({
                        'wix-code-ide-pane': true,
                        'above-editor':
                          this.props.devModeContext.type !==
                            constants.DEVELOPER_MODE.CONTEXT_TYPES.COLLECTION &&
                          Number(
                            this.props.editorSplitPosition.replace('%', ''),
                          ) < 50,
                      })}
                    >
                      {this.props.previewMode ? (
                        getConsoleComponent(this.props)
                      ) : (
                        <IDEContainer
                          loadWixCodeWithRepluggableInClassic={
                            this.props.loadWixCodeWithRepluggableInClassic
                          }
                          codeEditorComponent={this.props.codeEditorComponent}
                          toggleMeasureMaps={this.props.toggleMeasureMaps}
                          setIsWixCodeIDELoaded={
                            this.props.setIsWixCodeIDELoaded
                          }
                        />
                      )}
                    </Splitter.Pane>
                  ) : null}
                </Splitter>

                <OpenedPanels
                  isFullStagePanel
                  helpPanelProps={this.props.helpPanelProps}
                  isMobileEditor={this.props.isMobileEditor}
                  selectedComponents={this.props.selectedComponents}
                  withHelpPanel={false}
                />

                {this.props.shouldDisplayLayersPanel ? (
                  <LazyComponent
                    id="layers-panel-loader"
                    moduleName="panels.toolPanels.layersPanel"
                    onClose={this.props.hideLayersPanel}
                  />
                ) : null}

                {this.props.shouldDisplayQrCodeExposer ? (
                  <LazyComponent
                    id="mobile-preview-qrcode-exposer"
                    moduleName="mobileEditor.qrCodeExposer"
                  />
                ) : null}
              </Splitter.Pane>

              {this.shouldShowSectionedPanel() ||
              this.shouldShowPlatformSidePanel() ? (
                <Splitter.Pane
                  key="right-area"
                  id="right-panel"
                  className={this.getPlatformSidePanelClasses()}
                  size={`${this.props.sidePanelWidth}px`}
                  collapsed={
                    this.props.isRightPanelHidden ||
                    (!this.shouldShowSectionedPanel() &&
                      !this.shouldShowPlatformSidePanel())
                  }
                >
                  {this.shouldShowSectionedPanel() ? (
                    <LazyComponent
                      moduleName={
                        this.props.previewMode
                          ? 'dockableSections.sectionedPanelPreview'
                          : 'dockableSections.sectionedPanel'
                      }
                      previewMode={this.props.previewMode}
                      key="sectioned-panel"
                    />
                  ) : null}

                  {this.shouldShowPlatformSidePanel() ? (
                    <LazyComponent
                      moduleName="platformPanels.sidePanel"
                      previewMode={this.props.previewMode}
                      key="platform-side-panel"
                    />
                  ) : null}
                </Splitter.Pane>
              ) : null}
            </Splitter>
          </Splitter.Pane>
        </Splitter>
      </div>
    );
  }
}

export const SplitterWrapper = util.hoc.connect(
  util.hoc.STORES.EDITOR_API,
  mapStateToProps,
  mapDispatchToProps,
)(SplitterWrapperComponent);

SplitterWrapper.pure = SplitterWrapperComponent;
